import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import {isFirstLoad} from 'nutshell-core/utils/graphql-loading';

import type {BroadcastSortType, EditionStatus} from 'nutshell-graphql-types';
import type {
    GetBroadcasts as GetBroadcastsQuery,
    GetBroadcastsVariables as GetBroadcastsQueryVariables,
    BroadcastStubFragment as BroadcastStub,
} from 'nutshell-graphql-types';

import {getBroadcastListQueryVariables} from '../helpers';

import GET_BROADCASTS from './graphql/queries/get-broadcasts.graphql';

type Props = {
    isArchived: boolean;
    sortOrder: BroadcastSortType;
    searchQuery?: string;
    creatorUserId?: string;
    lastUpdatedTime?: string | null | undefined;
    statuses?: EditionStatus[];
    assigneeIds?: string[];
    children: (arg1: {
        broadcasts: BroadcastStub[];
        error: string | null | undefined;
        isLoading: boolean;
        isFirstLoad: boolean;
        fetchMoreRows: () => Promise<any>;
        hasNextPage: boolean;
    }) => React.ReactElement;
};

const LIMIT = 10;

export function BroadcastListQuery(props: Props) {
    const variables = getBroadcastListQueryVariables(
        props.isArchived,
        props.sortOrder,
        props.searchQuery,
        props.creatorUserId,
        props.lastUpdatedTime,
        props.statuses,
        props.assigneeIds
    );

    const {data, networkStatus, error, fetchMore, loading} = useQuery<
        GetBroadcastsQuery,
        GetBroadcastsQueryVariables
    >(GET_BROADCASTS, {
        variables: variables,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const broadcastsData = data ? data.broadcasts : null;
    const broadcasts = broadcastsData
        ? broadcastsData.edges.map((campaign) => {
              return campaign.node;
          })
        : [];

    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: LIMIT,
                after: broadcastsData ? broadcastsData.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetBroadcastsQuery,
                newResults: {
                    fetchMoreResult: GetBroadcastsQuery;
                }
            ) => {
                const oldBroadcasts = prev.broadcasts;
                const fetchedBroadcasts = newResults.fetchMoreResult.broadcasts;

                const newBroadcasts = {
                    ...oldBroadcasts,
                    pageInfo: fetchedBroadcasts.pageInfo,
                    edges: oldBroadcasts.edges.concat(fetchedBroadcasts.edges),
                } as const;

                return {broadcasts: newBroadcasts};
            },
        });
    };

    const hasNextPage = broadcastsData ? broadcastsData.pageInfo.hasNextPage : false;

    return props.children({
        broadcasts,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading: loading,
        isFirstLoad: isFirstLoad(networkStatus),
        fetchMoreRows,
        hasNextPage,
    });
}
